import { useCallback, useState, useRef, useEffect } from "react";
import { makeStyles } from "tss-react/mui";
import DyanamicDropdown from "./DynamicDropdown";
import { handleSort, formatAssetDisplayName } from "helpers/utils";

// Minimum width to ensure dropdown is wide enough for content
const MIN_DROPDOWN_WIDTH = 600;

const useStyles = makeStyles()((theme) => ({
  dropdownContainer: {
    position: "relative",
    width: "100%"
  }
}));

const ModelTaskAssetDropdown = ({
  assets,
  selectedAsset,
  onChange,
  count,
  onPageChange,
  page,
  handleServerSideSearch,
  disabled,
  isReadOnly,
  customCaptions,
  fetchData,
  preloadedSearch,
  showAssetReferences,
  handleCustomSort
}) => {
  const { classes } = useStyles();
  const [inputWidth, setInputWidth] = useState(MIN_DROPDOWN_WIDTH);
  const dropdownRef = useRef(null);

  // Measure the width of the input element
  useEffect(() => {
    if (dropdownRef.current) {
      const inputElement = dropdownRef.current.querySelector('.inputbox');
      if (inputElement) {
        // Use ResizeObserver to track changes in width
        const resizeObserver = new ResizeObserver(entries => {
          for (let entry of entries) {
            // Always use the larger of the minimum width or input width
            setInputWidth(Math.max(entry.contentRect.width, MIN_DROPDOWN_WIDTH));
          }
        });
        
        resizeObserver.observe(inputElement);
        
        // Initial measurement
        setInputWidth(Math.max(inputElement.offsetWidth, MIN_DROPDOWN_WIDTH));
        
        return () => {
          resizeObserver.disconnect();
        };
      }
    }
  }, []);

  // Format selected value for display
  const displayValue = selectedAsset ? {
    ...selectedAsset,
    displayName: formatAssetDisplayName(selectedAsset, showAssetReferences)
  } : null;

  // Set column width based on showAssetReferences
  const columnWidth = showAssetReferences ? "33%" : "50%";
  
  // Base header and columns (always include name and description)
  const dataHeader = [
    {
      id: 1,
      name: "Name",
      width: columnWidth,
      sortField: "assetno"
    },
    {
      id: 3,
      name: "Description",
      width: showAssetReferences ? "34%" : columnWidth,
      sortField: "description"
    }
  ];
  
  const columns = [
    {
      id: 1,
      name: "name",
    },
    {
      id: 3,
      name: showAssetReferences ? "assetReferenceDescription" : "description",
    }
  ];
  
  // Insert asset reference column if showAssetReferences is true
  if (showAssetReferences) {
    dataHeader.splice(1, 0, {
      id: 2,
      name: customCaptions?.assetReference ?? "Asset Reference",
      width: columnWidth,
      sortField: "referencename"
    });
    
    columns.splice(1, 0, {
      id: 2,
      name: "assetReferenceName",
    });
  }

  return (
    <div className={classes.dropdownContainer} ref={dropdownRef}>
      <DyanamicDropdown
        width={"100%"}
        dropDownActiveWidth={`${inputWidth}px`}
        dataSource={(assets ?? [])?.map((asset) => ({
          ...asset,
          id: asset?.siteAssetID,
          // Use assetReferenceName as unique key for comparison when name is empty
          uniqueKey: `${asset?.siteAssetID || ''}_${asset?.assetReferenceName || asset?.name || ''}`,
        }))}
        dataHeader={dataHeader}
        columns={columns}
        showHeader
        onChange={onChange}
        selectedValue={displayValue}
        onPageChange={onPageChange}
        page={page}
        selectdValueToshow="displayName"
        count={count}
        handleSort={handleSort}
        handleServerSideSort={handleCustomSort}
        handleServerSideSearch={handleServerSideSearch}
        isServerSide
        fetchData={fetchData}
        isReadOnly={isReadOnly}
        disabled={disabled}
        showClear
        onClear={() => onChange("")}
        placeholder={`Select ${customCaptions?.asset}`}
        PreloadedSearch={preloadedSearch}
        cacheDropDownData={false}
        useUniqueKey={true}
      />
    </div>
  );
};

export default ModelTaskAssetDropdown; 